import type { ReminderPreviewEditField } from '../components/ReminderPreviewInlineEdit'
import type { ChatMessage } from '../types/chat'

/**
 * Detect whether user text is still part of the reminder confirmation / edit flow
 * (so we should keep Yes/No enabled). Mirrors backend ``remind_me_intent`` loosely.
 */

function foldAsciiLower(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\u0300-\u036f/g, '')
    .toLowerCase()
}

function normalizeForMatch(text: string): string {
  let t = foldAsciiLower(text || '')
  t = t.replace(/[^\w\s]/g, ' ')
  t = t.replace(/\s+/g, ' ').trim()
  return t
}

/** Mirrors backend ``remind_me_intent`` (STT: continue / continu / continuer / contunye). */
const FR_CONTINUE_STEM = 'continu(?:er|e|es)?'
const FR_CONTINUE_STT_LOOSE = '(?:continu\\w{1,12}|contun\\w{1,12})'

function matchesReminderYesProceed(t: string): boolean {
  if (
    new RegExp(
      `^(yes|yeah|yep|ok|okay|proceed|confirm|continue|yes\\s+proceed(?:\\s+please)?|yes\\s+continue(?:\\s+please)?|oui|oui\\s+${FR_CONTINUE_STEM}|oui\\s+proceder|${FR_CONTINUE_STEM}|confirme|confirmer|valider|d\\s*accord|c\\s*est\\s+bon|parfait|entendu|allez\\s*y|vas\\s*y|bien\\s+sure)$`,
      'i',
    ).test(t)
  ) {
    return true
  }
  if (/^(yes|yeah|yep|oui|ok|okay|sure)\s*,?\s*proceed(\s+please)?(\s+\S+)*$/i.test(t)) {
    return true
  }
  if (/^(yes|yeah|yep|oui|ok|okay|sure)\s*,?\s*continue(\s+please)?(\s+\S+)*$/i.test(t)) {
    return true
  }
  if (/^(proceed|continue)(\s+please)?(\s+\S+)*$/i.test(t)) {
    return true
  }
  if (/^(yes|yeah|yep|oui)\s*,?\s*(go\s+ahead|do\s+it)(\s+\S+)*$/i.test(t)) {
    return true
  }
  if (
    new RegExp(
      `^(oui|ok|okay)\\s*,?\\s*${FR_CONTINUE_STEM}(\\s+stp|\\s+s\\s*il\\s*vous\\s*plait)?(\\s+\\S+)*$`,
      'i',
    ).test(t)
  ) {
    return true
  }
  if (new RegExp(`^${FR_CONTINUE_STEM}(\\s+stp|\\s+s\\s*il\\s*vous\\s*plait)?(\\s+\\S+)*$`, 'i').test(t)) {
    return true
  }
  if (/^(confirmer|valider|enregistrer)(\s+stp|\s+s\s*il\s*vous\s*plait)?(\s+\S+)*$/i.test(t)) {
    return true
  }
  if (
    /^(oui|ok|okay)\s*,?\s*(d\s*accord|c\s*est\s+bon|parfait|entendu|bien\s+sure|allez\s*y|vas\s*y)(\s+\S+)*$/i.test(
      t,
    )
  ) {
    return true
  }
  if (/^(d\s*accord|c\s*est\s+bon|parfait|entendu|bien\s+sure)(\s+\S+)*$/i.test(t)) {
    return true
  }
  if (new RegExp(`^oui\\s+${FR_CONTINUE_STT_LOOSE}(\\s+\\S+)*$`, 'i').test(t)) {
    return true
  }
  if (new RegExp(`^${FR_CONTINUE_STT_LOOSE}$`, 'i').test(t)) {
    return true
  }
  return false
}

/** True if this utterance should keep reminder Yes/No active (same intent). */
export function userTextIsReminderPreviewFollowUp(text: string): boolean {
  const raw = (text || '').trim()
  if (!raw) return false
  const t = normalizeForMatch(raw)

  if (matchesReminderYesProceed(t)) {
    return true
  }

  if (
    /^(no|nope|cancel|stop|abort|no\s+cancel|non|annule|annuler|non\s+annuler)$/.test(t)
  ) {
    return true
  }

  if (/\b(edit|change|modify|modifier|modifie|corriger|update)\b/i.test(raw)) {
    return true
  }

  if (/^edit\s+(purpose|day|time|objet|jour|heure)\s*$/i.test(raw.trim())) {
    return true
  }

  return false
}

/** True when the user is confirming or declining the reminder (not editing fields). */
export function userTextIsReminderPreviewYesNoFollowUp(text: string): boolean {
  if (!userTextIsReminderPreviewFollowUp(text)) return false
  return parseReminderPreviewEditField(text) == null
}

/**
 * Disable Yes/No/Edit on the active preview as soon as the user sends yes/no
 * (button label or voice), before the server round-trip completes.
 */
export function applyReminderPreviewInactiveBeforeYesNoFollowUp(
  messages: readonly ChatMessage[],
  userText: string,
): ChatMessage[] {
  if (!userTextIsReminderPreviewYesNoFollowUp(userText)) return [...messages]

  let targetId = findActionableReminderPreviewMessageId(messages)
  if (!targetId) {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (m.role !== 'assistant' || m.historySnapshot) continue
      if (m.reminderPreview && !m.reminderConfirmed && !m.reminderCancelled) {
        targetId = m.id
        break
      }
    }
  }
  if (!targetId) return [...messages]

  return messages.map((m) => {
    if (m.id !== targetId || m.reminderConfirmed || m.reminderCancelled) return m
    return {
      ...m,
      awaitingRemindConfirmation: false,
      reminderPreviewResolved: true,
    }
  })
}

/** Mirrors backend ``remind_me_intent`` edit-target detection for purpose / day / time only. */
export function parseReminderPreviewEditField(text: string): ReminderPreviewEditField | null {
  const raw = (text || '').trim()
  if (!raw) return null
  if (!/\b(edit|change|modify|modifier|modifie|corriger|update)\b/i.test(raw)) {
    return null
  }
  const low = foldAsciiLower(raw)
  const isFr =
    /\b(modifier|modifie|corriger)\b/.test(low) ||
    /\b(jour|date|heure|temps|objet)\b/.test(low)

  if ((isFr && (/\bheure\b/.test(low) || /\btemps\b/.test(low))) || /\btime\b/.test(low)) {
    return 'time'
  }
  if ((isFr && (/\bjour\b/.test(low) || /\bdate\b/.test(low))) || /\bday\b/.test(low)) {
    return 'day'
  }
  if ((isFr && /\bobjet\b/.test(low)) || /\bpurpose\b/.test(low)) {
    return 'purpose'
  }
  return null
}

export function reminderPreviewEditShouldOpenInline(text: string): boolean {
  const raw = (text || '').trim()
  if (!raw) return false
  return (
    /^edit\s+(purpose|day|time)\s*$/i.test(raw) ||
    /^modify(?:\s+the)?\s+(purpose|day|time)\s*$/i.test(raw) ||
    /^change(?:\s+the)?\s+(purpose|day|time)\s*$/i.test(raw) ||
    /^update(?:\s+the)?\s+(purpose|day|time)\s*$/i.test(raw) ||
    /^modifier\s+(?:l['’]?objet|le\s+jour|l['’]?heure)\s*$/i.test(raw) ||
    /^modifie(?:r)?\s+(?:l['’]?objet|le\s+jour|l['’]?heure)\s*$/i.test(raw) ||
    /^corriger\s+(?:l['’]?objet|le\s+jour|l['’]?heure)\s*$/i.test(raw)
  )
}

/** Latest reminder preview card that still accepts Yes/No/Edit actions. */
export function findActionableReminderPreviewMessageId(
  messages: readonly ChatMessage[],
): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role !== 'assistant' || m.historySnapshot) continue
    if (m.reminderYesNoSuperseded || m.reminderPreviewResolved || m.reminderConfirmed || m.reminderCancelled) {
      continue
    }
    if (m.reminderPreview && m.awaitingRemindConfirmation) return m.id
  }
  return null
}
